import {
  ProjectLanguage,
  SpecialVersionType,
  hexIdToHumanHexId,
} from "@hex/common";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ORG_ID } from "../../orgs";
import { ComponentRouteParams, Routes } from "../../route/routes";
import { useFeatureGates } from "../feature-gate/FeatureGateContext";
import { DEFAULT_COMPONENT_TITLE } from "../logic/shared.js";

import { useCreateComponentMutation } from "./NewComponentDialog.generated";

export interface UseCreateNewDefaultComponentResult {
  createPythonComponent: (newTab?: boolean) => Promise<void>;
  createRComponent: (newTab?: boolean) => Promise<void>;
}

export function useCreateNewDefaultComponent(): UseCreateNewDefaultComponentResult {
  const { components: componentsEnabled } = useFeatureGates();

  const navigate = useNavigate();
  const [createComponent] = useCreateComponentMutation();

  const createComponentCallback = useCallback(
    async (projectLanguage: ProjectLanguage, newTab?: boolean) => {
      if (!componentsEnabled) {
        throw new Error("Components are not enabled");
      }
      try {
        const { data } = await createComponent({
          variables: {
            name: DEFAULT_COMPONENT_TITLE,
            projectLanguage,
            description: null,
            kernelImage: null,
            kernelSize: null,
            cellIdsToCopy: null,
            autoPublish: false,
          },
        });
        if (data?.createHex?.id == null) {
          throw new Error("Creation failed");
        }
        const componentViewArgs: ComponentRouteParams = {
          hexLocator: hexIdToHumanHexId(
            data.createHex.id,
            data.createHex.title,
          ),
          version: SpecialVersionType.DRAFT,
        };
        if (newTab) {
          window.open(
            Routes.COMPONENT.href(ORG_ID, true, componentViewArgs),
            "_blank",
          );
        } else {
          Routes.COMPONENT.push(navigate, componentViewArgs);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [componentsEnabled, createComponent, navigate],
  );

  const createPythonComponent = useCallback(
    async (newTab?: boolean) => {
      await createComponentCallback(ProjectLanguage.PYTHON, newTab);
    },
    [createComponentCallback],
  );
  const createRComponent = useCallback(
    async (newTab?: boolean) => {
      await createComponentCallback(ProjectLanguage.R, newTab);
    },
    [createComponentCallback],
  );

  return { createPythonComponent, createRComponent };
}
